/* eslint react-hooks/exhaustive-deps: "warn" */

import { useRef } from 'react';

import cn from 'classnames';
import Link from 'next/link';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Icon } from 'ui/atoms/Icon/Icon';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DataSourcesSection.module.scss';

type Translate = (key: string) => string;

type Provenance = {
  key: 'registry' | 'derived' | 'enriched';
  icon: string;
  tag: string;
  source: string;
  fields: string[];
};

const getProvenance = (t: Translate): Provenance[] => [
  {
    key: 'registry',
    icon: 'estate',
    tag: t('Registry'),
    source: t('Read from the filing itself, across 400+ official sources. Carries the registry and the filing date.'),
    fields: [
      t('Registration number'),
      t('Legal form and status'),
      t('Registered address'),
      t('VAT and tax identifier'),
      t('Filed accounts, twenty years'),
      t('Shareholders and officers'),
    ],
  },
  {
    key: 'derived',
    icon: 'financial',
    tag: t('Derived'),
    source: t(
      'Resolved or calculated from registry filings. The inputs are primary data; the output is produced by us.',
    ),
    fields: [
      t('Group structure'),
      t('Ultimate beneficial owners'),
      t('Corporate linkage'),
      t('Credit score'),
      t('Recommended credit limit'),
    ],
  },
  {
    key: 'enriched',
    icon: 'unlock',
    tag: t('Enriched'),
    source: t('Resolved through a waterfall across more than ten partners, verified before it is returned.'),
    fields: [t('Executive work email'), t('Direct dial')],
  },
];

export const DataSourcesSection = () => {
  const { t } = useInt();
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.2 });

  return (
    <Section className={styles.root}>
      <div ref={ref} className={cn(styles.lead, revealed && styles.in)}>
        <TitleBlock align="left" title={t('Every field carries its data source.')} />
      </div>

      <div className={styles.grid}>
        {getProvenance(t).map((item, index) => (
          <div
            key={item.key}
            className={cn(styles.card, styles[item.key], revealed && styles.in)}
            style={{ transitionDelay: revealed ? `${index * 0.1}s` : '0s' }}
          >
            <span className={styles.cardIcon}>
              <Icon type={item.icon} size="md" />
            </span>

            <span className={styles.tag}>{item.tag}</span>
            <p className={styles.source}>{item.source}</p>

            <ul className={styles.fields}>
              {item.fields.map((field) => (
                <li key={field}>{field}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className={styles.foot}>
        <Link className={styles.footLink} href="/data-dictionary">
          {t('Field-level data dictionary')}
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path
              d="M2.5 7h9M8 3.5 11.5 7 8 10.5"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Link>
      </div>
    </Section>
  );
};
